import { AppointmentOrderItem } from '@spike/order-model';
import { FunctionComponent, useMemo } from 'react';
import MembershipIcon from '../Memberships/MembershipIcon';
import styles from './AppointmentData.module.css';
import PriceItem from './PriceItem';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Vehicle } from '@spike/vehicle-model';
import Appointment, { AppointmentDuration } from '@spike/appointment-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHouse } from '@fortawesome/pro-regular-svg-icons';
import { Room, RoomUnit } from '@spike/room-model';
import { getTrainingOrWalkingDurationMinutes } from 'utils/appointmentServiceDuration';

export interface AppointmentDataProps {
    appointmentItem: AppointmentOrderItem;
    className?: string;
}

const parseDaycareDurationOption = (appointment: Appointment): string | undefined => {
    const raw = appointment?.serviceVariableDuration;
    if (!raw) return undefined;

    let parsed: { duration_option?: string; durationOption?: string } | undefined;
    if (typeof raw === 'string') {
        try {
            parsed = JSON.parse(raw);
        } catch {
            return undefined;
        }
    } else {
        parsed = raw as { duration_option?: string; durationOption?: string };
    }

    return parsed?.duration_option ?? parsed?.durationOption;
};

export const getDuration = (appointment: Appointment) => {
    const durationOption = parseDaycareDurationOption(appointment);
    if (!durationOption) {
        return '';
    }

    if (durationOption === 'full_day') {
        return 'Full Day';
    }
    if (durationOption === 'half_day') {
        return 'Half Day';
    }
    if (durationOption === 'hourly') {
        return 'Hourly';
    }

    return '';
};

const getRange = (duration: AppointmentDuration) => {
    const durationInMinutes = duration?.to.clone().diff(duration?.from.clone(), 'minutes');

    if (durationInMinutes) {
        if (durationInMinutes <= 120) {
            return 'Up to 2 hours';
        } else if (durationInMinutes <= 240) {
            return 'Up to 4 hours';
        } else if (durationInMinutes <= 360) {
            return 'Up to 6 hours';
        } else if (durationInMinutes <= 480) {
            return 'Up to 8 hours';
        } else if (durationInMinutes <= 600) {
            return 'Up to 10 hours';
        } else if (durationInMinutes <= 720) {
            return 'Up to 12 hours';
        } else {
            return 'More than 12 hours';
        }
    }

    return '';
};

export const AppointmentData: FunctionComponent<AppointmentDataProps> = props => {
    const vehicles = useSelector<RootState, Vehicle[]>(state => state.vehicles.vehicles);
    const appointment = props.appointmentItem.appointment;
    const orderItem = props.appointmentItem.orderItem;
    const businessAreaId = props.appointmentItem.appointment.services[0]?.businessArea?.id;
    const rooms = useSelector<RootState, any>(state => state.rooms?.rooms);

    const hasMembershipApplied = orderItem.redeem !== undefined;
    const formatMoney2 = (value: unknown): string => {
        if (value === null || value === undefined) return '-';
        const n = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : Number(value as any);
        if (!Number.isFinite(n)) return '-';
        return n.toFixed(2);
    };

    const isDaycare = businessAreaId === 'daycare';
    const daycareDurationOption = isDaycare ? parseDaycareDurationOption(appointment) : undefined;
    const isDaycareHourly = isDaycare && daycareDurationOption === 'hourly';
    const hasDaycareOrderDiscount =
        hasMembershipApplied ||
        Number(orderItem.discount?.amount ?? '0') > 0 ||
        Boolean(orderItem.discount?.percentage);

    const daycareHoursRoundedUp = useMemo(() => {
        if (!isDaycareHourly) return 0;
        const durationMinutes = appointment.duration.to.clone().diff(appointment.duration.from.clone(), 'minutes');
        return Math.max(0, Math.ceil((durationMinutes || 0) / 60));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDaycareHourly, appointment.duration.from, appointment.duration.to]);

    const daycareHourlySubtotal = useMemo(() => {
        if (!isDaycareHourly) return undefined;
        const rate = Number(orderItem.amount ?? '0');
        const safeRate = Number.isFinite(rate) ? rate : 0;
        return safeRate * daycareHoursRoundedUp;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDaycareHourly, daycareHoursRoundedUp, orderItem.amount]);

    const daycareDisplayPrice = useMemo(() => {
        if (!isDaycare) return Number(orderItem.total ?? orderItem.amount ?? '0');

        const total = Number(orderItem.total ?? '0');
        if (isDaycareHourly && daycareHourlySubtotal != null) {
            if (Number.isFinite(total) && total > 0) {
                const rate = Number(orderItem.amount ?? '0');
                if (
                    !hasDaycareOrderDiscount &&
                    daycareHoursRoundedUp > 1 &&
                    Number.isFinite(rate) &&
                    Math.abs(total - rate) < 0.005
                ) {
                    return daycareHourlySubtotal;
                }
                return total;
            }
            return daycareHourlySubtotal;
        }

        if (Number.isFinite(total) && total > 0) {
            return total;
        }
        return Number(orderItem.amount ?? '0');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isDaycare,
        isDaycareHourly,
        daycareHourlySubtotal,
        daycareHoursRoundedUp,
        hasDaycareOrderDiscount,
        orderItem.total,
        orderItem.amount
    ]);

    const daycareOriginalPrice = useMemo(() => {
        if (!isDaycare) return undefined;
        if (hasMembershipApplied) {
            const original = Number(orderItem.originalPrice ?? orderItem.amount ?? '0');
            return Number.isFinite(original) ? original.toFixed(2) : undefined;
        }
        if (!hasDaycareOrderDiscount) {
            return undefined;
        }
        if (isDaycareHourly && daycareHourlySubtotal != null) {
            return daycareHourlySubtotal.toFixed(2);
        }
        const base = Number(orderItem.originalPrice ?? orderItem.amount ?? '0');
        return Number.isFinite(base) ? base.toFixed(2) : undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isDaycare,
        isDaycareHourly,
        daycareHourlySubtotal,
        hasMembershipApplied,
        hasDaycareOrderDiscount,
        orderItem.originalPrice,
        orderItem.amount
    ]);

    const isBoarding = businessAreaId === 'boarding';
    const boardingNightlyPrice = useMemo(() => {
        if (!isBoarding) return undefined;
        const nightly = Number(orderItem.amount ?? '0');
        if (Number.isFinite(nightly)) {
            return nightly;
        }
        const nights = Math.max(1, appointment.nightsCount ?? 1);
        const stayTotal = Number(orderItem.total ?? '0');
        return Number.isFinite(stayTotal) ? stayTotal / nights : 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBoarding, orderItem.amount, orderItem.total, appointment.nightsCount]);

    const boardingDisplayPrice = isBoarding ? boardingNightlyPrice : Number(orderItem.total ?? '0');
    const boardingOriginalPrice = useMemo(() => {
        if (!isBoarding) return undefined;
        if (hasMembershipApplied) {
            return Number(orderItem.originalPrice ?? '0').toFixed(2);
        }
        if (Number(orderItem.discount?.amount ?? '0') > 0 || orderItem.discount?.percentage) {
            const catalogNightly = Number(orderItem.originalPrice ?? orderItem.amount ?? '0');
            return Number.isFinite(catalogNightly) ? catalogNightly.toFixed(2) : undefined;
        }
        return undefined;
    }, [isBoarding, hasMembershipApplied, orderItem.originalPrice, orderItem.amount, orderItem.discount]);

    const firstLine = (
        <>
            <span className={styles.description}>{appointment.services[0].name}</span>
            {`· `}
        </>
    );
    const price = (
        <>
            <PriceItem
                price={formatMoney2(
                    isBoarding
                        ? boardingDisplayPrice
                        : isDaycare
                        ? daycareDisplayPrice
                        : orderItem.total ?? orderItem.amount
                )}
                originalPrice={
                    isBoarding
                        ? boardingOriginalPrice
                        : isDaycare
                        ? daycareOriginalPrice
                        : hasMembershipApplied
                        ? Number(orderItem.originalPrice ?? '0.00').toFixed(2)
                        : Number(orderItem.discount?.amount ?? '0') > 0 || orderItem.discount?.percentage
                        ? Number(orderItem.amount).toFixed(2)
                        : undefined
                }
                className={styles.price}
            />
            {isBoarding && <span className={styles.perNight}>/Night</span>}
        </>
    );

    const startHour = appointment.duration.from.clone().format('h:mm a');

    const serviceDurationMinutes =
        businessAreaId === 'training' || businessAreaId === 'walking'
            ? getTrainingOrWalkingDurationMinutes(appointment, businessAreaId)
            : appointment.duration.to.clone().diff(appointment.duration.from.clone(), 'minutes');

    const duration = `${serviceDurationMinutes}min`;

    const staffMember = appointment.services[0].staff?.firstName;
    const secondLine = `${startHour} · ${duration} · ${staffMember}`;
    const secondLineDaycareAndBoarding = `${startHour} · ${getDuration(appointment)} · ${getRange(
        appointment.duration
    )}`;

    const isTransportation = businessAreaId === 'transportation';
    const isTraining = businessAreaId === 'training';
    const isWalking = businessAreaId === 'walking';
    const isInHome =
        appointment.details?.training?.type === 'in_home' || appointment.details?.walking?.type === 'in_home';
    const isPickUp = appointment.details?.transportation?.type === 'pickup';

    const secondLineTransportation = `${appointment.duration.from.clone().format('h:mm a')} - ${appointment.duration.to
        .clone()
        .format('h:mm a')} · ${isPickUp ? 'Pick Up' : 'Drop Off'} · ${staffMember} (${
        vehicles?.find(vehicle => vehicle.id === appointment.details?.transportation?.vehicleId)?.name || ''
    })`;

    const address =
        appointment.details?.transportation?.address ||
        appointment.details?.training?.address ||
        appointment.details?.walking?.address ||
        '';

    const addressClient = address ? <span className={styles.address}>{address}</span> : null;

    const instructions = (
        appointment.details?.walking?.instructions || appointment.details?.training?.instructions
    )?.trim();
    const instructionsClient = instructions ? <span className={styles.instructions}>{instructions}</span> : null;

    const inHomeTag = (
        <div className={styles.inHomeServiceTag}>
            <FontAwesomeIcon
                icon={faHouse}
                className={styles.iconInHome}
            />
            <label>Home Service</label>
        </div>
    );

    const roomList = rooms?.page ?? rooms?.rooms ?? [];
    const room = useMemo(
        () =>
            roomList.find((r: Room) => r.units?.some((u: RoomUnit) => u.id === appointment.roomUnitId)) ||
            roomList.find((r: Room) => r.id === appointment.details?.boarding?.room),
        [roomList, appointment.roomUnitId]
    );
    const unit = useMemo(
        () => room?.units?.find((u: RoomUnit) => u.id === appointment.roomUnitId),
        [room, appointment.roomUnitId]
    );

    return (
        <>
            <div className={styles.firstLineContainer}>
                {hasMembershipApplied && <MembershipIcon className={styles.membershipIcon} />}
                <span className={styles.firstLineText}>
                    {firstLine}
                    {price}
                </span>
            </div>
            <span className={styles.secondLine}>
                {isDaycare && secondLineDaycareAndBoarding}
                {(isBoarding || isDaycare) && <></>}
                {(isBoarding || isDaycare) && (
                    <span className={isDaycare ? styles.roomUnitName : undefined}>
                        {`${room?.name ?? 'Unassigned'} - ${unit?.name ?? 'Unassigned'}`}
                    </span>
                )}
                {isTransportation && secondLineTransportation}
                {!isDaycare && !isBoarding && !isTransportation && secondLine}
                {(isTransportation || (isInHome && (isTraining || isWalking))) && addressClient}
                {isInHome && (isTraining || isWalking) && instructionsClient}
                {isInHome && (isTraining || isWalking) && inHomeTag}
            </span>
        </>
    );
};

export default AppointmentData;
